import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppService } from '@core/services/app/app-api';
import { OrgAndStockroomService } from '@core/services/org-and-stockroom/org-and-stockroom-api';
import { OrganizationService } from '@core/services/organization/organization-api';
import { ToastService } from '@core/services/toast-api';
import { UserMaintenanceService } from '@core/services/user-maintenance/user-maintenance-api';
import { MaterialsModule } from '@shared/materials.module';
import { SharedModule } from '@shared/shared.module';

import { NewStockroomRequestService } from '../new-stockroom-request';
import {
  CLONE_STOCKROOM,
  NEW_STOCKROOM,
  NEW_STOCKROOM_COMMENT_MAX_WORDS,
  NEW_STOCKROOM_MODES,
  NEW_STOCKROOM_NAME_MAX,
  REQUEST_TYPES,
  STOCKROOM_MODE_NORMAL,
  STOCKROOM_MODE_POU,
} from '../new-stockroom-request.constant';

type NameValidationState = 'unknown' | 'valid' | 'invalid';

@Component({
  selector: 'app-dialog-new-stockroom-request-form',
  templateUrl: './dialog-new-stockroom-request.html',
  styleUrls: ['./dialog-new-stockroom-request.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialsModule, SharedModule],
})
export class DialogNewStockroomRequestFormComponent implements OnInit {
  titleText = 'New Stock Room';
  subtitleText = 'Set up a new stock room for your organization';

  stockroomForm!: FormGroup;
  submitting = false;
  isEditMode = false;
  loadingMasterStockrooms = false;
  validatingName = false;

  readonly STOCKROOM_MODES = NEW_STOCKROOM_MODES;
  readonly STOCKROOM_MODE_POU = STOCKROOM_MODE_POU;
  readonly NEW_STOCKROOM = NEW_STOCKROOM;
  readonly CLONE_STOCKROOM = CLONE_STOCKROOM;
  readonly REQUEST_TYPES = REQUEST_TYPES;
  readonly STOCKROOM_NAME_MAX = NEW_STOCKROOM_NAME_MAX;
  readonly COMMENT_MAX_WORDS = NEW_STOCKROOM_COMMENT_MAX_WORDS;
  readonly SHIP_TO_CUSTOM_VALUE = 'custom';

  organizations: any[] = [];
  filteredOrganizations: any[] = [];
  orgFilterText = '';
  stockrooms: any[] = [];
  filteredStockrooms: any[] = [];
  stockroomFilterText = '';
  loadingStockrooms = false;

  shipTos: string[] = [];
  customShipTo = false;
  currentStockroomMode = STOCKROOM_MODE_NORMAL;
  showStockroomModeSection = true;
  currentOrgId: string | number | null = null;

  masterStockrooms: any[] = [];
  activeUsers: any[] = [];
  filteredActiveUsers: any[] = [];
  selectedUserAccessUsers: any[] = [];
  userAccessFilterText = '';
  nameValidationState: NameValidationState = 'unknown';

  private loggedInUserId: string | number | null = null;
  private loggedInStockroomId: string | number | null = null;
  private validationRequestId = 0;
  private originalStockroomName = '';
  private originalOrgId: string | number | null = null;
  private editMasterStockroomId: string | number | null = null;
  private editShipToNumber: string | number | null = null;
  private selectedStockroomShipToNumber: string | number | null = null;
  private stockroomDetailsRequestId = 0;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DialogNewStockroomRequestFormComponent>,
    private newStockroomService: NewStockroomRequestService,
    private appService: AppService,
    private orgAndStockroomService: OrgAndStockroomService,
    private orgService: OrganizationService,
    private userMaintenanceService: UserMaintenanceService,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    this.isEditMode = !!this.data?.id || this.data?.mode === 'edit';
    this.titleText = this.isEditMode ? 'Edit Stock Room' : 'New Stock Room';
    this.initForm();
    this.bindLoggedInUser();
    this.bindCurrentOrgStockroom();
    this.bindFormWatchers();
    this.loadOrganizations();
    void this.loadActiveUsers();
    void this.loadShipTos();
    this.applyEditData();
  }

  private initForm(): void {
    this.stockroomForm = this.fb.group({
      requestType: [NEW_STOCKROOM, Validators.required],
      requestedForUserName: [{ value: '', disabled: true }, Validators.required],
      userAccess: [[]],
      userAccessSelection: [null],
      orgId: [null, Validators.required],
      organizationName: [{ value: null, disabled: true }, Validators.required],
      stockroomId: [null, Validators.required],
      stockroomName: [{ value: null, disabled: true }, Validators.required],
      newStockroomName: ['', [Validators.required, Validators.maxLength(this.STOCKROOM_NAME_MAX)]],
      stockroomMode: [STOCKROOM_MODE_NORMAL, Validators.required],
      masterStockroomId: [null],
      shipToNumber: [null, Validators.required],
      customShipToNumber: [''],
      comments: ['', this.maxWordsValidator(this.COMMENT_MAX_WORDS)],
    });
  }

  private bindLoggedInUser(): void {
    const u = this.appService.getUserData() || {};
    this.loggedInUserId = u.userId || u.id || u.internalUserId || u.userInternalId || null;
    const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
      || u.fullName || u.userName || u.loginId || u.email || '';

    this.stockroomForm.patchValue({ requestedForUserName: fullName });
  }

  private bindCurrentOrgStockroom(): void {
    const raw = this.orgAndStockroomService.getStockroomAndOrgName();
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const organizationName = parsed?.organization?.orgName
        || parsed?.organization?.organizationName
        || parsed?.organization?.name
        || '';
      const orgId = parsed?.organization?.id
        || parsed?.organization?.orgId
        || parsed?.organization?.organizationId
        || null;
      const stockroomName = parsed?.stockroom?.stockroomName
        || parsed?.stockroom?.name
        || parsed?.stockroom?.stockRoomName
        || '';
      const stockroomId = parsed?.stockroom?.id
        || parsed?.stockroom?.stockroomId
        || parsed?.stockroom?.stockRoomId
        || null;
      const stockroomMode = parsed?.stockroom?.stockroomMode
        || parsed?.stockroom?.stockRoomMode
        || parsed?.stockroom?.mode
        || STOCKROOM_MODE_NORMAL;

      this.stockroomForm.patchValue({
        orgId,
        organizationName,
        stockroomId,
        stockroomName,
      });
      this.currentOrgId = orgId;
      this.loggedInStockroomId = stockroomId;
      this.currentStockroomMode = this.normalizeStockroomModeValue(stockroomMode);
    } catch {
      // Leave fields empty if session payload parse fails.
    }
  }

  private bindFormWatchers(): void {
    this.stockroomForm.get('newStockroomName')?.valueChanges.subscribe(() => {
      this.nameValidationState = 'unknown';
      this.removeControlError('newStockroomName', 'duplicate');
    });

    this.stockroomForm.get('orgId')?.valueChanges.subscribe(() => {
      this.nameValidationState = 'unknown';
      this.removeControlError('newStockroomName', 'duplicate');
    });

    this.stockroomForm.get('userAccess')?.valueChanges.subscribe(() => {
      this.syncSelectedUserAccessUsers();
    });

    this.stockroomForm.get('shipToNumber')?.valueChanges.subscribe((value: string) => {
      if (value !== this.SHIP_TO_CUSTOM_VALUE) {
        this.customShipTo = false;
        this.stockroomForm.patchValue({ customShipToNumber: '' }, { emitEvent: false });
        this.stockroomForm.get('customShipToNumber')?.clearValidators();
        this.stockroomForm.get('customShipToNumber')?.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private async loadOrganizations(): Promise<void> {
    try {
      const res = await this.newStockroomService.getOrganizations();
      this.organizations = this.extractList(res);
      this.filteredOrganizations = [...this.organizations];

      if (!this.currentOrgId && !this.isEditMode && this.organizations.length) {
        const first = this.organizations[0];
        this.currentOrgId = this.getOrgId(first);
        this.stockroomForm.patchValue({
          orgId: this.currentOrgId,
          organizationName: this.getOrgName(first),
        });
      } else if (this.currentOrgId) {
        const currentOrg = this.organizations.find((org: any) =>
          String(this.getOrgId(org)) === String(this.currentOrgId)
        );
        if (currentOrg && !this.stockroomForm.get('organizationName')?.value) {
          this.stockroomForm.patchValue({ organizationName: this.getOrgName(currentOrg) });
        }
      }

      this.applyEditData();
      const orgId = this.stockroomForm.get('orgId')?.value || this.currentOrgId;
      if (orgId) {
        await this.loadStockroomsForOrganization(orgId);
      }
    } catch (err) {
      this.toastService.show('error', 'Unable to load organizations', '', true, 3000);
    }
  }

  private async loadActiveUsers(): Promise<void> {
    try {
      const res = await this.userMaintenanceService.getUserList(
        'ACTIVE',
        0,
        -1,
        ['lastModifiedDate', 'desc'],
        undefined,
        this.getLoggedInImContext()
      );
      this.activeUsers = this.extractList(res)
        .map((user: any) => ({
          ...user,
          userIdValue: this.getActiveUserId(user),
          displayName: this.getActiveUserDisplayName(user),
        }))
        .filter((user: any) => this.hasValue(user.userIdValue) && this.hasValue(user.displayName));
      this.filteredActiveUsers = [...this.activeUsers];
      this.applyEditUserAccessSelection();
    } catch (err) {
      this.activeUsers = [];
      this.filteredActiveUsers = [];
    }
  }

  onOrgChange(orgId: string | number): void {
    this.currentOrgId = orgId;
    const selectedOrg = this.organizations.find((org: any) =>
      String(this.getOrgId(org)) === String(orgId)
    );
    this.selectedStockroomShipToNumber = null;
    this.stockroomDetailsRequestId++;
    this.stockroomForm.patchValue({
      organizationName: selectedOrg ? this.getOrgName(selectedOrg) : this.stockroomForm.get('organizationName')?.value,
      stockroomId: null,
      stockroomName: null,
      masterStockroomId: null,
    });
    this.loggedInStockroomId = null;
    void this.loadStockroomsForOrganization(orgId);
    if (this.stockroomForm.get('stockroomMode')?.value === STOCKROOM_MODE_POU && orgId) {
      void this.loadMasterStockrooms(orgId);
    }
  }

  onStockroomChange(stockroomId: string | number): void {
    const selectedStockroom = this.stockrooms.find((stockroom: any) =>
      String(this.getStockroomId(stockroom)) === String(stockroomId)
    );
    this.applySelectedStockroom(selectedStockroom || { id: stockroomId });
  }

  onShipToChange(value: string): void {
    this.customShipTo = value === this.SHIP_TO_CUSTOM_VALUE;
    const customControl = this.stockroomForm.get('customShipToNumber');

    if (this.customShipTo) {
      customControl?.setValidators([Validators.required]);
    } else {
      customControl?.clearValidators();
      customControl?.reset('');
    }

    customControl?.updateValueAndValidity();
  }

  onEnterNewShipTo(): void {
    this.stockroomForm.patchValue({ shipToNumber: this.SHIP_TO_CUSTOM_VALUE });
    this.onShipToChange(this.SHIP_TO_CUSTOM_VALUE);
  }

  filterOrg(text: string): void {
    this.orgFilterText = text || '';
    const q = this.orgFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredOrganizations = [...this.organizations];
      return;
    }

    this.filteredOrganizations = this.organizations.filter((org: any) =>
      this.getOrgName(org).toLowerCase().includes(q)
    );
  }

  onOrgPanelOpened(opened: boolean): void {
    if (opened) {
      this.orgFilterText = '';
      this.filteredOrganizations = [...this.organizations];
    }
  }

  onStockroomPanelOpened(opened: boolean): void {
    if (opened) {
      this.stockroomFilterText = '';
      this.filteredStockrooms = [...this.stockrooms];
    }
  }

  filterStockroom(text: string): void {
    this.stockroomFilterText = text || '';
    const query = this.stockroomFilterText.trim().toLowerCase();
    this.filteredStockrooms = !query
      ? [...this.stockrooms]
      : this.stockrooms.filter((stockroom: any) =>
        this.getStockroomName(stockroom).toLowerCase().includes(query)
      );
  }

  onUserAccessPanelOpened(opened: boolean): void {
    if (opened) {
      this.userAccessFilterText = '';
      this.filteredActiveUsers = [...this.activeUsers];
    }
  }

  filterUserAccess(text: string): void {
    this.userAccessFilterText = text || '';
    const query = this.userAccessFilterText.trim().toLowerCase();
    this.filteredActiveUsers = !query
      ? [...this.activeUsers]
      : this.activeUsers.filter((user: any) =>
        [user.displayName, this.getActiveUserLoginId(user), this.getActiveUserEmail(user), String(user.userIdValue)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query)
      );
  }

  onUserAccessSelected(userId: string | number): void {
    if (!this.hasValue(userId) || this.isUserAccessSelected(userId)) {
      this.stockroomForm.patchValue({ userAccessSelection: null }, { emitEvent: false });
      return;
    }

    const currentSelection = this.stockroomForm.get('userAccess')?.value || [];
    this.stockroomForm.patchValue({
      userAccess: [...currentSelection, userId],
      userAccessSelection: null,
    });
  }

  removeUserAccess(userId: string | number): void {
    const currentSelection = this.stockroomForm.get('userAccess')?.value || [];
    this.stockroomForm.patchValue({
      userAccess: currentSelection.filter((selectedId: any) => String(selectedId) !== String(userId)),
    });
  }

  isUserAccessSelected(userId: string | number): boolean {
    const currentSelection = this.stockroomForm.get('userAccess')?.value || [];
    return currentSelection.some((selectedId: any) => String(selectedId) === String(userId));
  }

  private async loadStockroomsForOrganization(orgId: string | number): Promise<void> {
    if (!orgId) {
      this.stockrooms = [];
      this.filteredStockrooms = [];
      this.stockroomForm.patchValue({ stockroomId: null, stockroomName: null });
      this.selectedStockroomShipToNumber = null;
      this.applyShipToSelection(null);
      return;
    }

    try {
      this.loadingStockrooms = true;
      const res = await this.newStockroomService.getStockrooms(orgId);
      const topLevelStockrooms = res.stockroomHierarchy;
      const flatStockrooms = this.flattenStockrooms(topLevelStockrooms)
        .sort((a: any, b: any) => this.getStockroomName(a).localeCompare(this.getStockroomName(b)));

      this.stockrooms = flatStockrooms;
      this.filteredStockrooms = [...this.stockrooms];

      const selectedStockroom = this.getDefaultSelectedStockroom();
      this.applySelectedStockroom(selectedStockroom || this.stockrooms[0] || null);
    } catch (err) {
      this.stockrooms = [];
      this.filteredStockrooms = [];
      this.toastService.show('error', 'Unable to load stockrooms', '', true, 3000);
    } finally {
      this.loadingStockrooms = false;
    }
  }

  onRequestTypeChange(value: string): void {
    const isClone = value === CLONE_STOCKROOM;
    this.showStockroomModeSection = !isClone;

    if (isClone) {
      this.stockroomForm.patchValue({
        stockroomMode: this.currentStockroomMode,
        userAccess: [],
        userAccessSelection: null,
      }, { emitEvent: false });
      this.syncSelectedUserAccessUsers();
      return;
    }

    if (!this.stockroomForm.get('stockroomMode')?.value) {
      this.stockroomForm.patchValue({ stockroomMode: this.currentStockroomMode || STOCKROOM_MODE_NORMAL }, { emitEvent: false });
    }
  }

  onStockroomModeChange(value: string): void {
    const masterControl = this.stockroomForm.get('masterStockroomId');

    if (value === STOCKROOM_MODE_POU) {
      masterControl?.setValidators([Validators.required]);
      const orgId = this.currentOrgId || this.stockroomForm.get('orgId')?.value;
      if (orgId) {
        void this.loadMasterStockrooms(orgId);
      }
    } else {
      masterControl?.clearValidators();
      this.stockroomForm.patchValue({ masterStockroomId: null });
      this.masterStockrooms = [];
    }

    masterControl?.updateValueAndValidity();
  }

  private async loadMasterStockrooms(orgId: string | number): Promise<void> {
    if (!orgId) {
      this.masterStockrooms = [];
      this.stockroomForm.patchValue({ masterStockroomId: null });
      return;
    }

    try {
      this.loadingMasterStockrooms = true;
      const res = await this.newStockroomService.getMasterStockrooms(orgId, this.getLoggedInImContext());
      this.masterStockrooms = this.extractList(res);

      const editMasterStockroomId = this.getEditMasterStockroomId();
      const first = this.masterStockrooms.find((s: any) =>
        this.hasValue(editMasterStockroomId) && String(this.getMasterStockroomId(s)) === String(editMasterStockroomId)
      ) || this.masterStockrooms[0];
      this.stockroomForm.patchValue({
        masterStockroomId: first ? this.getMasterStockroomId(first) : null,
      });
    } catch (err) {
      this.masterStockrooms = [];
      this.stockroomForm.patchValue({ masterStockroomId: null });
      this.toastService.show('error', 'Unable to load master stockrooms', '', true, 3000);
    } finally {
      this.loadingMasterStockrooms = false;
    }
  }

  private async loadShipTos(): Promise<void> {
    try {
      const userType = (this.appService.getUserData()?.userType || '').toString();
      const isAdminUser = ['SUPER_USER', 'COUNTRY_ADMIN'].includes(userType);
      const res = isAdminUser
        ? await this.orgService.getAdminShipTos(this.getLoggedInImContext())
        : await this.orgService.getShipTos(this.getLoggedInImContext());

      this.shipTos = Array.isArray(res) ? res : res?.data || [];
      if (this.hasValue(this.selectedStockroomShipToNumber)) {
        this.applyShipToSelection(this.selectedStockroomShipToNumber);
      } else {
        this.applyEditShipToSelection();
      }
    } catch (err) {
      this.shipTos = [];
    }
  }

  onStockroomNameBlur(): void {
    const control = this.stockroomForm.get('newStockroomName');
    const trimmed = (control?.value || '').toString().trim();
    if (control && control.value !== trimmed) {
      control.setValue(trimmed);
    }

    if (!trimmed || control?.hasError('maxlength') || control?.hasError('required')) {
      return;
    }

    this.validateStockroomName(true);
  }

  private async validateStockroomName(showToast: boolean, showSuccessToast = true): Promise<boolean> {
    const orgId = this.stockroomForm.get('orgId')?.value;
    const stockroomName = (this.stockroomForm.get('newStockroomName')?.value || '').toString().trim();

    if (!orgId || !stockroomName) {
      return false;
    }

    if (this.isEditMode
      && String(orgId) === String(this.originalOrgId)
      && stockroomName.toLowerCase() === this.originalStockroomName.toLowerCase()) {
      this.nameValidationState = 'valid';
      this.removeControlError('newStockroomName', 'duplicate');
      return true;
    }

    const requestId = ++this.validationRequestId;
    this.validatingName = true;

    try {
      const res = await this.newStockroomService.validateStockroomName(
        orgId,
        stockroomName,
        this.getLoggedInImContext()
      );
      if (requestId !== this.validationRequestId) {
        return false;
      }

      const isValid = !!res?.isValid;
      this.nameValidationState = isValid ? 'valid' : 'invalid';

      if (isValid) {
        this.removeControlError('newStockroomName', 'duplicate');
      } else {
        this.addControlError('newStockroomName', 'duplicate');
      }

      if (showToast && res?.status && (showSuccessToast || !isValid)) {
        this.toastService.show(isValid ? 'success' : 'error', res.status, '', true, 3000);
      }

      return isValid;
    } catch (err) {
      this.nameValidationState = 'unknown';
      if (showToast) {
        this.toastService.show('error', 'Unable to validate stockroom name', '', true, 3000);
      }
      return false;
    } finally {
      if (requestId === this.validationRequestId) {
        this.validatingName = false;
      }
    }
  }

  async submit(): Promise<void> {
    this.stockroomForm.markAllAsTouched();
    this.updateMasterStockroomValidation();

    if (this.stockroomForm.invalid) {
      this.toastService.show('error', 'Please fill all required fields and try again.', '', true, 3000);
      return;
    }

    const isNameValid = await this.validateStockroomName(true, false);
    if (!isNameValid) {
      return;
    }

    const payload = this.buildSubmitPayload();

    try {
      this.submitting = true;
      const res = await this.newStockroomService.submitNewStockroomRequest(payload);
      this.dialogRef.close({ submitted: true, payload });
      this.toastService.show('success', res?.status || 'New stockroom request submitted successfully', '', true, 3000);
    } catch (err) {
      // The HTTP interceptor already displays the API's validation message.
      if (err instanceof HttpErrorResponse && err.status === 400
        && (err.error?.label || err.error?.errorMessage)) {
        return;
      }
      this.toastService.show('error', this.extractErrorMessage(err) || 'Could not submit the new stockroom request.', '', true, 3000);
    } finally {
      this.submitting = false;
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  isInvalid(controlName: string): boolean {
    const control = this.stockroomForm.get(controlName);
    if (!control) return false;
    return control.invalid && (control.touched || control.dirty);
  }

  hasError(controlName: string, errorKey: string): boolean {
    const control = this.stockroomForm.get(controlName);
    if (!control) return false;
    return !!control.errors?.[errorKey] && (control.touched || control.dirty);
  }

  getOrgId(org: any): any {
    return org?.id || org?.organizationId || org?.orgId;
  }

  getOrgName(org: any): string {
    return (org?.orgName || org?.organizationName || org?.name || '').toString();
  }

  getActiveUserId(user: any): any {
    return user?.id || user?.userId || user?.userID || user?.loginId;
  }

  getActiveUserLoginId(user: any): string {
    return (user?.loginId || user?.email || '').toString();
  }

  getActiveUserEmail(user: any): string {
    return (user?.loginId || user?.email || user?.emailAddress || '').toString();
  }

  getActiveUserDisplayName(user: any): string {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    const loginId = this.getActiveUserLoginId(user);
    const email = this.getActiveUserEmail(user);

    if (fullName && loginId && fullName !== loginId) {
      return `${fullName} (${loginId})`;
    }

    if (fullName && email && fullName !== email) {
      return `${fullName} (${email})`;
    }

    return fullName || loginId || email || String(this.getActiveUserId(user) ?? '');
  }

  getActiveUserName(user: any): string {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.fullName || user?.userName || this.getActiveUserLoginId(user) || String(this.getActiveUserId(user) ?? '');
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  getMasterStockroomId(stockroom: any): any {
    return stockroom?.skGuid || stockroom?.id || stockroom?.stockroomId;
  }

  getMasterStockroomName(stockroom: any): string {
    return (stockroom?.stockroomName || stockroom?.name || '').toString();
  }

  getStockroomId(stockroom: any): any {
    return stockroom?.id || stockroom?.stockroomId || stockroom?.stockRoomId || stockroom?.skGuid;
  }

  getStockroomName(stockroom: any): string {
    return (stockroom?.stockroomName || stockroom?.name || stockroom?.stockRoomName || '').toString();
  }

  private buildSubmitPayload(): any {
    const v = this.stockroomForm.getRawValue();
    const payload: any = {
      ...(this.isEditMode && this.hasValue(this.data?.id) ? { id: this.data.id } : {}),
      ticketType: 'STOCKROOM_REQUEST',
      requestType: v.requestType,
      loggedInOrgId: this.toNumberOrOriginal(this.currentOrgId || v.orgId),
      loggedInStockroomId: this.toNumberOrOriginal(v.stockroomId || this.loggedInStockroomId),
      reportingUserId: this.toNumberOrOriginal(this.loggedInUserId),
      newStockroomName: v.newStockroomName,
      shipToNumber: v.shipToNumber === this.SHIP_TO_CUSTOM_VALUE
        ? v.customShipToNumber
        : v.shipToNumber,
      comments: v.comments || '',
    };

    if (v.requestType !== CLONE_STOCKROOM) {
      payload.stockroomMode = this.getSubmitStockroomMode(v.stockroomMode);
      payload.selectedUserIds = this.normalizeSelectedUserIds(v.userAccess);
    } else {
      payload.sourceStockroomId = this.toNumberOrOriginal(v.stockroomId || this.loggedInStockroomId);
    }

    if (v.stockroomMode === STOCKROOM_MODE_POU) {
      const master = this.masterStockrooms.find((s: any) =>
        String(this.getMasterStockroomId(s)) === String(v.masterStockroomId)
      );

      if (master) {
        payload.masterStockroomId = this.toNumberOrOriginal(this.getMasterStockroomId(master));
      }
    }

    return payload;
  }

  private getSubmitStockroomMode(stockroomMode: any): string {
    return this.normalizeStockroomModeValue(stockroomMode) === STOCKROOM_MODE_POU ? 'pou' : 'master';
  }

  private updateMasterStockroomValidation(): void {
    const masterControl = this.stockroomForm.get('masterStockroomId');
    if (!masterControl) return;

    if (this.stockroomForm.get('stockroomMode')?.value === STOCKROOM_MODE_POU) {
      masterControl.setValidators([Validators.required]);
    } else {
      masterControl.clearValidators();
      this.stockroomForm.patchValue({ masterStockroomId: null });
    }

    masterControl.updateValueAndValidity();
  }

  private toNumberOrOriginal(value: any): any {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isNaN(n) ? value : n;
  }

  private toNumber(value: any): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
  }

  private getLoggedInImContext(): { orgId?: number; stockroomId?: number } | undefined {
    const orgId = this.toNumber(this.currentOrgId || this.stockroomForm?.get('orgId')?.value);
    const stockroomId = this.toNumber(this.loggedInStockroomId || this.stockroomForm?.get('stockroomId')?.value);

    if (!orgId && !stockroomId) {
      return undefined;
    }

    return {
      ...(orgId ? { orgId } : {}),
      ...(stockroomId ? { stockroomId } : {}),
    };
  }

  private normalizeSelectedUserIds(value: any): number[] {
    const values = Array.isArray(value) ? value : [];
    return values
      .map(item => Number(item))
      .filter(item => !Number.isNaN(item));
  }

  private extractList(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (!res || typeof res !== 'object') return [];
    const keys = [
      'imeorganizationBaseDTO',
      'imeuserBaseDTO',
      'imestockroomBaseDTO',
      'data',
      'organizations',
      'users',
      'userList',
      'stockrooms',
      'items',
      'results',
      'organizationList',
      'stockroomList',
      'list',
    ];

    for (const key of keys) {
      if (Array.isArray(res[key])) return res[key];
      if (res[key] && typeof res[key] === 'object') {
        for (const nestedKey of keys) {
          if (Array.isArray(res[key][nestedKey])) return res[key][nestedKey];
        }
      }
    }

    return [];
  }

  private flattenStockrooms(stockrooms: any[]): any[] {
    const flat: any[] = [];
    if (!Array.isArray(stockrooms)) {
      return flat;
    }

    for (const stockroom of stockrooms) {
      if (this.getStockroomName(stockroom)) {
        flat.push(stockroom);
      }

      const children = stockroom?.childStockroom || stockroom?.childStockrooms || stockroom?.children;
      if (Array.isArray(children)) {
        flat.push(...this.flattenStockrooms(children));
      }
    }

    return flat;
  }

  private getDefaultSelectedStockroom(): any {
    const selectedId = this.stockroomForm.get('stockroomId')?.value || this.loggedInStockroomId;
    if (this.hasValue(selectedId)) {
      const selectedById = this.stockrooms.find((stockroom: any) =>
        String(this.getStockroomId(stockroom)) === String(selectedId)
      );
      if (selectedById) {
        return selectedById;
      }
    }

    const currentName = this.stockroomForm.get('stockroomName')?.value;
    if (currentName) {
      return this.stockrooms.find((stockroom: any) =>
        this.getStockroomName(stockroom) === currentName
      );
    }

    return null;
  }

  private applySelectedStockroom(stockroom: any): void {
    if (!stockroom) {
      this.loggedInStockroomId = null;
      this.selectedStockroomShipToNumber = null;
      this.stockroomForm.patchValue({ stockroomId: null, stockroomName: null });
      this.applyShipToSelection(null);
      return;
    }

    const stockroomId = this.getStockroomId(stockroom);
    this.loggedInStockroomId = stockroomId;
    this.stockroomForm.patchValue({
      stockroomId,
      stockroomName: this.getStockroomName(stockroom),
    });
    void this.loadSelectedStockroomDetails(stockroomId);
  }

  private async loadSelectedStockroomDetails(stockroomId: string | number): Promise<void> {
    if (!this.hasValue(stockroomId)) {
      return;
    }

    const requestId = ++this.stockroomDetailsRequestId;

    try {
      const details = await this.newStockroomService.getStockroomDetails(stockroomId);
      if (requestId !== this.stockroomDetailsRequestId) {
        return;
      }

      const shipToNumber = this.getStockroomShipToNumber(details);
      this.selectedStockroomShipToNumber = shipToNumber;
      this.applyShipToSelection(shipToNumber);
    } catch {
      if (requestId === this.stockroomDetailsRequestId) {
        this.selectedStockroomShipToNumber = null;
      }
    }
  }

  private getStockroomShipToNumber(stockroomDetails: any): any {
    return stockroomDetails?.customerShipNo
      || stockroomDetails?.shipToNumber
      || stockroomDetails?.shipToNo
      || stockroomDetails?.shipTo
      || stockroomDetails?.shippingAndBilling?.shipTo
      || stockroomDetails?.shippingAndBilling?.shipToNumber
      || null;
  }

  private applyShipToSelection(shipToNumber: any): void {
    const customControl = this.stockroomForm.get('customShipToNumber');

    if (!this.hasValue(shipToNumber)) {
      this.customShipTo = false;
      this.stockroomForm.patchValue({
        shipToNumber: null,
        customShipToNumber: '',
      }, { emitEvent: false });
      customControl?.clearValidators();
      customControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const shipToValue = `${shipToNumber}`;
    const isKnownShipTo = this.shipTos.some((shipTo: string) => `${shipTo}` === shipToValue);

    if (isKnownShipTo) {
      this.customShipTo = false;
      this.stockroomForm.patchValue({
        shipToNumber: shipToValue,
        customShipToNumber: '',
      }, { emitEvent: false });
      customControl?.clearValidators();
      customControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.customShipTo = true;
    this.stockroomForm.patchValue({
      shipToNumber: this.SHIP_TO_CUSTOM_VALUE,
      customShipToNumber: shipToValue,
    }, { emitEvent: false });
    customControl?.setValidators([Validators.required]);
    customControl?.updateValueAndValidity({ emitEvent: false });
  }

  private addControlError(controlName: string, errorKey: string): void {
    const control = this.stockroomForm.get(controlName);
    if (!control) return;
    control.setErrors({ ...(control.errors || {}), [errorKey]: true });
  }

  private removeControlError(controlName: string, errorKey: string): void {
    const control = this.stockroomForm.get(controlName);
    if (!control || !control.errors?.[errorKey]) return;

    const errors = { ...control.errors };
    delete errors[errorKey];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private maxWordsValidator(maxWords: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value || '').toString().trim();
      if (!value) return null;

      const words = value.split(/\s+/).filter(Boolean);
      return words.length > maxWords
        ? { maxWords: { max: maxWords, actual: words.length } }
        : null;
    };
  }

  private extractErrorMessage(err: any): string {
    return err?.error?.status || err?.error?.errorMessage || err?.status || err?.message || '';
  }

  private applyEditData(): void {
    if (!this.isEditMode || !this.stockroomForm) {
      return;
    }

    const source = this.data?.raw || this.data || {};
    const orgId = source.orgId || source.organizationId || source.org?.id || source.org?.organizationId || source.org?.orgId;
    const stockroomName = source.newStockroomName || source.stockroomName || source.stockRoomName || '';
    const stockroomMode = this.normalizeStockroomModeValue(source.stockroomMode || source.stockRoomMode || source.mode || STOCKROOM_MODE_NORMAL);
    const shipToNumber = source.shipToNumber || source.shipTo || null;
    const requestedForUserName = source.requestedForUserName || source.userName || source.createdByName || source.requestedBy
      || this.stockroomForm.get('requestedForUserName')?.value;

    this.originalStockroomName = `${stockroomName || ''}`.trim();
    this.originalOrgId = this.hasValue(orgId) ? orgId : null;
    this.editMasterStockroomId = source.masterStockroomId || source.masterStockRoomId || source.parentStockroomId || null;
    this.editShipToNumber = this.hasValue(shipToNumber) ? shipToNumber : null;

    this.stockroomForm.patchValue({
      requestedForUserName,
      orgId: this.hasValue(orgId) ? orgId : (this.currentOrgId || this.stockroomForm.get('orgId')?.value),
      userAccess: this.normalizeEditUserAccess(source.selectedUserIds ?? source.selectedUsers ?? source.userAccess),
      newStockroomName: stockroomName,
      stockroomMode,
      masterStockroomId: this.editMasterStockroomId,
      shipToNumber: this.editShipToNumber,
      customShipToNumber: '',
      comments: source.comments || source.comment || '',
    }, { emitEvent: false });

    if (stockroomMode === STOCKROOM_MODE_POU && this.hasValue(orgId)) {
      void this.loadMasterStockrooms(orgId);
    }

    this.applyEditShipToSelection();
    this.applyEditUserAccessSelection();
  }

  private normalizeEditUserAccess(value: any): Array<string | number> {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item: any) => {
        if (item && typeof item === 'object') {
          return this.getActiveUserId(item);
        }

        return item;
      })
      .filter((item: any) => this.hasValue(item));
  }

  private normalizeStockroomModeValue(mode: any): string {
    const value = `${mode || ''}`.trim().toLowerCase();

    if (value === STOCKROOM_MODE_NORMAL || value === STOCKROOM_MODE_POU || value === 'master') {
      return value;
    }

    if (value.includes('normal')) {
      return STOCKROOM_MODE_NORMAL;
    }
    if (value.includes('master')) {
      return 'master';
    }
    if (value.includes('pou')) {
      return STOCKROOM_MODE_POU;
    }

    return STOCKROOM_MODE_NORMAL;
  }

  private getEditMasterStockroomId(): any {
    const source = this.data?.raw || this.data || {};
    return source.masterStockroomId || source.masterStockRoomId || source.parentStockroomId || this.editMasterStockroomId;
  }

  private applyEditShipToSelection(): void {
    if (!this.isEditMode || !this.stockroomForm || !this.hasValue(this.editShipToNumber)) {
      return;
    }

    this.applyShipToSelection(this.editShipToNumber);
  }

  private applyEditUserAccessSelection(): void {
    if (!this.isEditMode || !this.stockroomForm) {
      return;
    }

    const source = this.data?.raw || this.data || {};
    const selectedUserIds = this.normalizeEditUserAccess(source.selectedUserIds ?? source.selectedUsers ?? source.userAccess);
    if (!selectedUserIds.length) {
      return;
    }

    this.stockroomForm.patchValue({ userAccess: selectedUserIds }, { emitEvent: false });
    this.syncSelectedUserAccessUsers();
  }

  private syncSelectedUserAccessUsers(): void {
    const selectedIds = this.stockroomForm?.get('userAccess')?.value || [];
    if (!Array.isArray(selectedIds)) {
      this.selectedUserAccessUsers = [];
      return;
    }

    this.selectedUserAccessUsers = selectedIds
      .filter((selectedId: any) => this.hasValue(selectedId))
      .map((selectedId: any) => {
        const user = this.activeUsers.find((activeUser: any) =>
          String(activeUser.userIdValue) === String(selectedId)
        );

        return user || {
          userIdValue: selectedId,
          displayName: String(selectedId),
        };
      });
  }
}
